import { useContext } from 'react'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import { launchModal } from '@domain-group/javascript-utils'

import { mergeAndStringifyEntityObjects } from '@functions/entity'

import useDispatch from '@hooks/useDispatch'
import useSelector from '@hooks/useSelector'

import * as integrationActions from '@redux/modules/integration'

import defaultFormState, { requiredFields } from '@models/integration'

import PageContext from '@contexts/pageContext'
import type { AppDispatch } from '@redux/store'
import type { DeleteParams } from '@redux/modules/types'

import type { IntegrationModel, IntegrationRequestOptions } from '@models/types'

import integrationPlatforms from '@functions/integrationPlatform'

type DuplicateIntegrationParams = {
  dispatch: AppDispatch,
  integration: IntegrationModel,
  integrationParams: { integrable_id: number, integrable_type: string },
  requestOptions?: IntegrationRequestOptions,
}

const duplicateIntegration = (params: DuplicateIntegrationParams) => {
  const {
    dispatch, integration, integrationParams, requestOptions,
  } = params
  const { duplicateIntegration: duplicateFn } = integrationActions

  const updatedParams = {
    id: integration.id,
    ...integrationParams,
  }

  return dispatch(duplicateFn(updatedParams, requestOptions))
}

const stringifyObjectKeys: (keyof IntegrationModel)[] = ['data']

type CreateOrUpdateIntegrationParams = {
  dispatch: AppDispatch,
  integrationParams: Partial<IntegrationModel>,
}

const createOrUpdateIntegration = (params: CreateOrUpdateIntegrationParams) => {
  const { dispatch, integrationParams } = params
  const { createOrUpdateIntegration: updateFn } = integrationActions

  const updatedParams = mergeAndStringifyEntityObjects({}, integrationParams, stringifyObjectKeys)

  // @ts-expect-error TS2345
  return dispatch(updateFn(updatedParams))
}

type DeleteIntegrationParams = {
  dispatch: AppDispatch,
  integration: DeleteParams<IntegrationModel>,
}

const deleteIntegration = (params: DeleteIntegrationParams) => {
  const { dispatch, integration } = params
  const { deleteIntegration: deleteFn } = integrationActions

  return dispatch(deleteFn(integration))
}

type CustomFormOptions = {
  customDeps?: string[],
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useIntegrationForm(
  integration: Partial<IntegrationModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const {
    customRequiredFields = [],
    customDeps = [],
    onBlurFn,
    validateOn,
  } = options || {}

  const integrationForm = useForm(
    defaultFormState,
    {
      entity: integration, onBlurFn, requiredFields: [...requiredFields, ...customRequiredFields], validateOn,
    },
    [...customDeps, integration.id, integration.cache_key],
  )

  return {
    ...integrationForm,
  }
}

export function useRelations(integration: Partial<IntegrationModel> = {}) {
  const {
    integrable_id,
    integrable_type,
  } = integration

  const { agencies, users } = useSelector(reduxState => reduxState.entities)

  // @ts-expect-error TS2538
  const agency = integrable_type === 'Agency' && agencies[integrable_id] ? agencies[integrable_id] || {} : {}
  // @ts-expect-error TS2538
  const user = integrable_type === 'User' && users[integrable_id] ? users[integrable_id] || {} : {}

  return {
    agency,
    user,
  }
}

function useIntegration(initEntity: Partial<IntegrationModel> = {}) {
  // @ts-expect-error TS2322
  const { entity: integration }: { entity: IntegrationModel } = useLatestEntity(initEntity, 'integrations')

  const { service_provider } = integration

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  const { agency, user } = useRelations(integration)

  const title = integrationPlatforms.find(integrationPlatform => integrationPlatform.key === service_provider)?.title

  const {
    creating, deleting, loadedIds, loading, updating,
  } = useSelector(reduxState => reduxState.integrations)

  return {
    callbacks: {
      createOrUpdateIntegration: (integrationParams: Partial<IntegrationModel>) => (
        createOrUpdateIntegration({ integrationParams, dispatch })
      ),
      createOrEditIntegration: () => launchModal({
        callbacks,
        modalKey: 'CreateOrEditIntegrationModal',
        payload: { integration },
      }),
      manageUserIntegration: () => launchModal({
        callbacks,
        modalKey: 'ManageUserIntegrationModal',
        payload: { integration },
      }),
      deleteIntegration: () => deleteIntegration({ dispatch, integration }),
      duplicateIntegration: (
        integrationParams: DuplicateIntegrationParams['integrationParams'],
        requestOptions?: IntegrationRequestOptions,
      ) => (
        duplicateIntegration({
          dispatch, integration, integrationParams, requestOptions,
        })
      ),
    },
    agency,
    creating,
    deleting,
    integration,
    loading: loading && !loadedIds.includes(integration.id),
    title,
    updating,
    user,
  }
}

export type UseIntegrationPayload = ReturnType<typeof useIntegration>

export default useIntegration
